import React, { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X, Cookie, Shield, BarChart3, Target, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface CookieSettingsProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (preferences: CookiePreferences) => void;
}

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const CookieSettings = ({ isVisible, onClose, onSave }: CookieSettingsProps) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  const handleSave = () => { onSave(preferences); onClose(); };
  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true, preferences: true };
    onSave(all); onClose();
  };
  const handleRejectAll = () => {
    const min = { necessary: true, analytics: false, marketing: false, preferences: false };
    onSave(min); onClose();
  };

  const cookieCategories = [
    {
      key: "necessary" as keyof CookiePreferences,
      icon: Shield,
      iconColor: "text-emerald-500",
      iconBg: "bg-emerald-500/10 border-emerald-500/20",
      title: isAr ? 'ملفات تعريف الارتباط الضرورية' : 'Necessary Cookies',
      description: isAr
        ? 'هذه الملفات ضرورية لعمل الموقع بشكل صحيح ولا يمكن تعطيلها.'
        : 'Essential for the website to function properly. Cannot be disabled.',
      required: true,
    },
    {
      key: "analytics" as keyof CookiePreferences,
      icon: BarChart3,
      iconColor: "text-blue-500",
      iconBg: "bg-blue-500/10 border-blue-500/20",
      title: isAr ? 'ملفات تعريف الارتباط التحليلية' : 'Analytics Cookies',
      description: isAr
        ? 'تساعدنا في فهم كيفية تفاعل الزوار مع موقعنا بشكل مجهول.'
        : 'Help us understand how visitors interact with our website anonymously.',
      required: false,
    },
    {
      key: "marketing" as keyof CookiePreferences,
      icon: Target,
      iconColor: "text-orange-500",
      iconBg: "bg-orange-500/10 border-orange-500/20",
      title: isAr ? 'ملفات تعريف الارتباط التسويقية' : 'Marketing Cookies',
      description: isAr
        ? 'تُستخدم لعرض إعلانات ذات صلة وجذابة عبر المواقع.'
        : 'Used to display relevant and engaging advertisements across websites.',
      required: false,
    },
    {
      key: "preferences" as keyof CookiePreferences,
      icon: Cookie,
      iconColor: "text-purple-500",
      iconBg: "bg-purple-500/10 border-purple-500/20",
      title: isAr ? 'ملفات تعريف ارتباط التفضيلات' : 'Preference Cookies',
      description: isAr
        ? 'تتذكر اختياراتك وتفضيلاتك لتجربة مخصصة.'
        : 'Remember your choices and preferences for a personalized experience.',
      required: false,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="w-full max-w-lg max-h-[90vh] overflow-hidden"
            >
              <div className="bg-card rounded-2xl border border-border/60 shadow-2xl shadow-black/25 overflow-hidden flex flex-col max-h-[90vh]">

                {/* Gradient accent bar */}
                <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary flex-shrink-0" />

                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/20 flex items-center justify-center">
                      <Cookie className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-foreground">
                        {isAr ? 'إعدادات ملفات تعريف الارتباط' : 'Cookie Settings'}
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        {isAr ? 'تحكم في تفضيلاتك' : 'Control your preferences'}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-accent/50">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Content */}
                <div className="px-4 sm:px-5 pb-2 overflow-y-auto flex-1">
                  <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                    {isAr
                      ? 'اختر أنواع ملفات تعريف الارتباط التي تسمح بها على موقعنا.'
                      : 'Choose which types of cookies you allow on our website.'
                    }
                  </p>

                  <div className="space-y-3">
                    {cookieCategories.map((category) => {
                      const IconComponent = category.icon;
                      const isChecked = preferences[category.key];

                      return (
                        <div
                          key={category.key}
                          className={`rounded-xl border p-3 sm:p-4 transition-colors duration-200 ${
                            isChecked && !category.required
                              ? 'border-primary/30 bg-primary/5'
                              : 'border-border/40 bg-muted/20'
                          } ${category.required ? 'border-border/30 bg-muted/10' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 h-9 w-9 rounded-lg border flex items-center justify-center ${category.iconBg}`}>
                              <IconComponent className={`h-4 w-4 ${category.iconColor}`} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-foreground">
                                    {category.title}
                                  </span>
                                  {category.required && (
                                    <span className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full border border-border/40">
                                      <Lock className="h-2.5 w-2.5" />
                                      {isAr ? 'مطلوب' : 'Required'}
                                    </span>
                                  )}
                                </div>
                                {category.required ? (
                                  <div className="h-5 w-9 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-end pr-1">
                                    <div className="h-3 w-3 rounded-full bg-primary/60" />
                                  </div>
                                ) : (
                                  <Switch
                                    checked={isChecked}
                                    onCheckedChange={(checked) =>
                                      setPreferences(prev => ({ ...prev, [category.key]: checked }))
                                    }
                                  />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {category.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-4 border-t border-border/40 bg-muted/5 flex-shrink-0 flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRejectAll}
                    className="h-9 px-4 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {isAr ? 'رفض الكل' : 'Reject All'}
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSave}
                      className="h-9 px-4 text-xs border-border/60"
                    >
                      {isAr ? 'حفظ' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAcceptAll}
                      className="h-9 px-4 text-xs bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-semibold"
                    >
                      {isAr ? 'قبول الكل' : 'Accept All'}
                    </Button>
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieSettings;
